import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { axiosGet } from '../utils/axiosGet';
import { getCollectionRowNames } from '../utils/getCollectionRowNames';
import { filteringTableFormatDraftNomination } from '../utils/filteringTableFormatDraftNomination';
import { useLoggedInUserContextProvider } from './LoggedInUserContext';
import { devError } from '../utils/devLog';

const AdminCollections = createContext();
const paginationLimit = '&pagination[limit]=6000';

const initialState = {
  candidates: {},
  committeeArray: [],
  candidateStateArray: [],
  candidateArray: [],
  draftNominationArray: [],
  nominationArray: [],
  proposerSeconder: [],
  nominationStateArray: [],
  nominationSupporterArray: [],
  nominationDeclarationArray: [],
  coiDeclarationArray: [],
  coiDeclarationArchiveArray: [],
  biasTrainingArray: [],
  nominationRefereeArray: [],
  refereeReports: [],
  refereeReportArchive: [],
  candidateScoresArray: [],
  secretariesArray: [],
  councilMembersArray: [],
  refreshAdminCollections: false,
  refreshNominations: false,
  refreshCommittees: false,
  refreshSecretaries: false,
  refreshCouncilMembers: false,
  refreshCOI: 0,
  coiUpdateInProgress: false,
  isLoading: true,
  error: null,
};

const adminCollectionsReducer = (state, action) => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload, isLoading: false };
    case 'LOAD_ALL_COMPLETE':
      return {
        ...state,
        ...action.payload,
        isLoading: false,
        error: null,
      };
    case 'LOAD_NOMINATIONS_COMPLETE':
      return {
        ...state,
        nominationArray:
          action.payload.nominationArray || state.nominationArray,
        draftNominationArray:
          action.payload.draftNominationArray || state.draftNominationArray,
      };
    case 'LOAD_COMMITTEES_COMPLETE':
      return {
        ...state,
        committeeArray: action.payload || state.committeeArray,
      };
    case 'LOAD_SECRETARIES_COMPLETE':
      return {
        ...state,
        secretariesArray: action.payload || state.secretariesArray,
      };
    case 'LOAD_COUNCIL_MEMBERS_COMPLETE':
      return {
        ...state,
        councilMembersArray: action.payload || state.councilMembersArray,
      };
    case 'UPDATE_SINGLE_ITEM':
      return {
        ...state,
        [action.arrayName]: state[action.arrayName].map((item) =>
          item.id === action.item.id ? action.item : item,
        ),
      };
    case 'ADD_OR_UPDATE_ITEM':
      return {
        ...state,
        [action.arrayName]: (() => {
          const existingIndex = state[action.arrayName].findIndex(
            (item) => item.id === action.item.id,
          );
          if (existingIndex >= 0) {
            const newArray = [...state[action.arrayName]];
            newArray[existingIndex] = action.item;
            return newArray;
          }
          return [...state[action.arrayName], action.item];
        })(),
      };
    case 'SET_REFRESH_FLAG':
      return { ...state, [action.flagName]: action.value };
    case 'INCREMENT_REFRESH_COI':
      return { ...state, refreshCOI: state.refreshCOI + 1 };
    default:
      return state;
  }
};

export const AdminCollectionsProvider = ({ children }) => {
  const [state, dispatch] = useReducer(adminCollectionsReducer, initialState);
  const { originalUserDetails } = useLoggedInUserContextProvider();

  const memoizedUserId = useMemo(
    () => originalUserDetails?.id,
    [originalUserDetails?.id],
  );

  const memoizedUserRoles = useMemo(
    () => originalUserDetails?.roles || [],
    [originalUserDetails?.roles],
  );

  const triggerCOIRefresh = useCallback(
    async (declarationId) => {
      dispatch({ type: 'INCREMENT_REFRESH_COI' });

      if (memoizedUserId) {
        try {
          const response = await axiosGet(
            `coi-declarations/${declarationId}?populate=*`,
          );

          const updatedDeclaration = response?.data?.data;
          if (updatedDeclaration) {
            dispatch({
              type: 'ADD_OR_UPDATE_ITEM',
              arrayName: 'coiDeclarationArray',
              item: updatedDeclaration,
            });
          }
        } catch (error) {
          devError('Error refreshing COI declaration:', error);
        }
      }
    },
    [memoizedUserId],
  );

  const triggerNominationRefresh = useCallback(
    async (nominationId) => {
      if (memoizedUserId) {
        try {
          const response = await axiosGet(`nominations/${nominationId}`);

          const updatedNomination = response?.data?.data;
          if (updatedNomination) {
            dispatch({
              type: 'ADD_OR_UPDATE_ITEM',
              arrayName: 'nominationArray',
              item: updatedNomination,
            });
          }
        } catch (error) {
          devError('Error refreshing nomination:', error);
        }
      }
    },
    [memoizedUserId],
  );

  const triggerNominationStateRefresh = useCallback(
    async (nominationId) => {
      if (memoizedUserId) {
        try {
          const response = await axiosGet(
            `nomination-states?filters[nominationId][$eq]=${nominationId}&populate=*`,
          );

          const updatedNominationState = response?.data?.data?.[0];
          if (updatedNominationState) {
            dispatch({
              type: 'ADD_OR_UPDATE_ITEM',
              arrayName: 'nominationStateArray',
              item: updatedNominationState,
            });
          }
        } catch (error) {
          devError('Error refreshing nomination state:', error);
        }
      }
    },
    [memoizedUserId],
  );

  const triggerBiasTrainingRefresh = useCallback(
    async (trainingId) => {
      if (memoizedUserId) {
        try {
          const response = await axiosGet(`bias-trainings/${trainingId}`);

          const updatedTraining = response?.data?.data;
          if (updatedTraining) {
            dispatch({
              type: 'ADD_OR_UPDATE_ITEM',
              arrayName: 'biasTrainingArray',
              item: updatedTraining,
            });
          }
        } catch (error) {
          devError('Error refreshing bias training:', error);
        }
      }
    },
    [memoizedUserId],
  );

  const triggerCandidateScoresRefresh = useCallback(
    async (scoreId) => {
      if (memoizedUserId) {
        try {
          const response = await axiosGet(
            `candidate-scores/${scoreId}?populate=*`,
          );

          const updatedScore = response?.data?.data;
          if (updatedScore) {
            dispatch({
              type: 'ADD_OR_UPDATE_ITEM',
              arrayName: 'candidateScoresArray',
              item: updatedScore,
            });
          }
        } catch (error) {
          devError('Error refreshing candidate score:', error);
        }
      }
    },
    [memoizedUserId],
  );

  const triggerCommitteeRefresh = useCallback(async () => {
    if (memoizedUserId) {
      try {
        const response = await axiosGet('chair-committees?populate=*');

        dispatch({
          type: 'LOAD_COMMITTEES_COMPLETE',
          payload: response?.data?.data || [],
        });
      } catch (error) {
        devError('Error refreshing committees:', error);
      }
    }
  }, [memoizedUserId]);

  const triggerSecretariesRefresh = useCallback(async () => {
    if (memoizedUserId) {
      try {
        const response = await axiosGet(
          'secretaries?populate=users_permissions_user',
        );

        dispatch({
          type: 'LOAD_SECRETARIES_COMPLETE',
          payload: response?.data?.data || [],
        });
      } catch (error) {
        devError('Error refreshing secretaries:', error);
      }
    }
  }, [memoizedUserId]);

  const triggerCouncilMembersRefresh = useCallback(async () => {
    if (memoizedUserId) {
      try {
        const response = await axiosGet(
          'council-members?populate=users_permissions_user',
        );

        dispatch({
          type: 'LOAD_COUNCIL_MEMBERS_COMPLETE',
          payload: response?.data?.data || [],
        });
      } catch (error) {
        devError('Error refreshing council members:', error);
      }
    }
  }, [memoizedUserId]);

  const setCandidateScoresArray = useCallback((newArray) => {
    dispatch({
      type: 'SET_REFRESH_FLAG',
      flagName: 'candidateScoresArray',
      value: newArray,
    });
  }, []);

  const setRefereeReports = useCallback((newArray) => {
    dispatch({
      type: 'SET_REFRESH_FLAG',
      flagName: 'refereeReports',
      value: newArray,
    });
  }, []);

  const setRefereeReportArchive = useCallback((newArray) => {
    dispatch({
      type: 'SET_REFRESH_FLAG',
      flagName: 'refereeReportArchive',
      value: newArray,
    });
  }, []);

  const setRefreshAdminCollections = useCallback((value) => {
    dispatch({
      type: 'SET_REFRESH_FLAG',
      flagName: 'refreshAdminCollections',
      value,
    });
  }, []);

  const setRefreshNominations = useCallback((value) => {
    dispatch({
      type: 'SET_REFRESH_FLAG',
      flagName: 'refreshNominations',
      value,
    });
  }, []);

  const setRefreshCommittees = useCallback((value) => {
    dispatch({
      type: 'SET_REFRESH_FLAG',
      flagName: 'refreshCommittees',
      value,
    });
  }, []);

  const setRefreshSecretaries = useCallback((value) => {
    dispatch({
      type: 'SET_REFRESH_FLAG',
      flagName: 'refreshSecretaries',
      value,
    });
  }, []);

  const setRefreshCouncilMembers = useCallback((value) => {
    dispatch({
      type: 'SET_REFRESH_FLAG',
      flagName: 'refreshCouncilMembers',
      value,
    });
  }, []);

  const setCOIUpdateInProgress = useCallback((value) => {
    dispatch({
      type: 'SET_REFRESH_FLAG',
      flagName: 'coiUpdateInProgress',
      value,
    });
  }, []);

  useEffect(() => {
    if (!memoizedUserId) return;

    const loadAllAdminCollections = async () => {
      dispatch({ type: 'SET_LOADING', payload: true });

      try {
        const isAdmin =
          memoizedUserRoles.includes('admin') ||
          memoizedUserRoles.includes('super-admin');
        const isSCChair = memoizedUserRoles.includes('committee-chair');
        const isSCMember = memoizedUserRoles.includes('committee-member');
        const isSecretary = memoizedUserRoles.includes('secretary');
        const isObserver = memoizedUserRoles.includes('council-observer');
        const isCouncilMember = memoizedUserRoles.includes('council-member');

        const shouldLoadRefereeReports =
          isAdmin ||
          isSCChair ||
          isSCMember ||
          isSecretary ||
          isObserver ||
          isCouncilMember;

        const baseRequests = [
          axiosGet(`candidates?${paginationLimit}`),
          axiosGet(`chair-committees?populate=*${paginationLimit}`),
          axiosGet(`candidate-states?${paginationLimit}`),
          axiosGet(`nominations?${paginationLimit}`),
          axiosGet(`nomination-drafts?${paginationLimit}`),
          axiosGet(`proposer-seconders?populate=*${paginationLimit}`),
          axiosGet(`nomination-states?populate=*${paginationLimit}`),
          axiosGet(`nomination-supporters?populate=*${paginationLimit}`),
          axiosGet(`nomination-declarations?populate=*${paginationLimit}`),
          axiosGet(`coi-declarations?populate=*${paginationLimit}`),
          axiosGet(`bias-trainings?${paginationLimit}`),
          axiosGet(`nomination-referees?populate=*${paginationLimit}`),
          axiosGet(
            `referee-report-archives?populate[report][populate][0]=report${paginationLimit}`,
          ),
          axiosGet(`candidate-scores?populate=*${paginationLimit}`),
          axiosGet(
            `secretaries?populate=users_permissions_user${paginationLimit}`,
          ),
          axiosGet(
            `council-members?populate=users_permissions_user${paginationLimit}`,
          ),
        ];

        if (shouldLoadRefereeReports) {
          baseRequests.push(
            axiosGet(
              `referee-reports?filters[refereeArchived][$eq]=false&populate=*${paginationLimit}`,
            ),
          );
        }

        if (isAdmin) {
          baseRequests.push(
            axiosGet(`coi-declaration-archives?populate=*${paginationLimit}`),
          );
        }

        const allRequests = await Promise.all(baseRequests);

        const [
          candidatesRes,
          committeeRes,
          candidateStatesRes,
          nominationsRes,
          nominationDraftsRes,
          proposerSecondersRes,
          nominationStatesRes,
          nominationSupportersRes,
          nominationDeclarationsRes,
          coiDeclarationsRes,
          biasTrainingsRes,
          nominationRefereesRes,
          refereeReportArchiveRes,
          candidateScoresRes,
          secretariesRes,
          councilMembersRes,
        ] = allRequests;

        let nextIndex = 16;
        let refereeReportsRes;
        let coiDeclarationArchiveRes;

        if (shouldLoadRefereeReports) {
          refereeReportsRes = allRequests[nextIndex];
          nextIndex += 1;
        }

        if (isAdmin) {
          coiDeclarationArchiveRes = allRequests[nextIndex];
          nextIndex += 1;
        }

        const formattedDraftNomination = filteringTableFormatDraftNomination(
          nominationDraftsRes?.data?.data,
        );

        const allData = {
          candidates: getCollectionRowNames(candidatesRes?.data?.data) || {},
          candidateArray: candidatesRes?.data?.data || [],
          committeeArray: committeeRes?.data?.data || [],
          candidateStateArray: candidateStatesRes?.data?.data || [],
          nominationArray: nominationsRes?.data?.data || [],
          draftNominationArray: formattedDraftNomination || [],
          proposerSeconder: proposerSecondersRes?.data?.data || [],
          nominationStateArray: nominationStatesRes?.data?.data || [],
          nominationSupporterArray: nominationSupportersRes?.data?.data || [],
          nominationDeclarationArray:
            nominationDeclarationsRes?.data?.data || [],
          coiDeclarationArray: coiDeclarationsRes?.data?.data || [],
          coiDeclarationArchiveArray:
            coiDeclarationArchiveRes?.data?.data || [],
          biasTrainingArray: biasTrainingsRes?.data?.data || [],
          nominationRefereeArray: nominationRefereesRes?.data?.data || [],
          refereeReportArchive: refereeReportArchiveRes?.data?.data || [],
          candidateScoresArray: candidateScoresRes?.data?.data || [],
          secretariesArray: secretariesRes?.data?.data || [],
          councilMembersArray: councilMembersRes?.data?.data || [],
          refereeReports: refereeReportsRes?.data?.data || [],
        };

        dispatch({ type: 'LOAD_ALL_COMPLETE', payload: allData });
      } catch (error) {
        devError('Failed to load admin collections:', error);
        dispatch({ type: 'SET_ERROR', payload: error.message });
      }
    };

    loadAllAdminCollections();
  }, [state.refreshAdminCollections, memoizedUserId, memoizedUserRoles]);

  useEffect(() => {
    if (!memoizedUserId) return;

    const loadNominations = async () => {
      try {
        const [nominationsRes, nominationDraftsRes] = await Promise.all([
          axiosGet('nominations'),
          axiosGet('nomination-drafts'),
        ]);

        const formattedDraftNomination = filteringTableFormatDraftNomination(
          nominationDraftsRes?.data?.data,
        );

        dispatch({
          type: 'LOAD_NOMINATIONS_COMPLETE',
          payload: {
            nominationArray: nominationsRes?.data?.data || [],
            draftNominationArray: formattedDraftNomination || [],
          },
        });
      } catch (error) {
        devError('Failed to load nominations:', error);
      }
    };

    loadNominations();
  }, [state.refreshNominations, memoizedUserId]);

  useEffect(() => {
    if (!memoizedUserId || !state.refreshCommittees) return;

    const loadCommittees = async () => {
      try {
        const response = await axiosGet('chair-committees?populate=*');

        dispatch({
          type: 'LOAD_COMMITTEES_COMPLETE',
          payload: response?.data?.data || [],
        });

        dispatch({
          type: 'SET_REFRESH_FLAG',
          flagName: 'refreshCommittees',
          value: false,
        });
      } catch (error) {
        devError('Error refreshing committees:', error);
      }
    };

    loadCommittees();
  }, [state.refreshCommittees, memoizedUserId]);

  useEffect(() => {
    if (!memoizedUserId || !state.refreshSecretaries) return;

    const loadSecretaries = async () => {
      try {
        const response = await axiosGet(
          'secretaries?populate=users_permissions_user',
        );

        dispatch({
          type: 'LOAD_SECRETARIES_COMPLETE',
          payload: response?.data?.data || [],
        });

        dispatch({
          type: 'SET_REFRESH_FLAG',
          flagName: 'refreshSecretaries',
          value: false,
        });
      } catch (error) {
        devError('Error refreshing secretaries:', error);
      }
    };

    loadSecretaries();
  }, [state.refreshSecretaries, memoizedUserId]);

  useEffect(() => {
    if (!memoizedUserId || !state.refreshCouncilMembers) return;

    const loadCouncilMembers = async () => {
      try {
        const response = await axiosGet(
          'council-members?populate=users_permissions_user',
        );

        dispatch({
          type: 'LOAD_COUNCIL_MEMBERS_COMPLETE',
          payload: response?.data?.data || [],
        });

        dispatch({
          type: 'SET_REFRESH_FLAG',
          flagName: 'refreshCouncilMembers',
          value: false,
        });
      } catch (error) {
        devError('Error refreshing council members:', error);
      }
    };

    loadCouncilMembers();
  }, [state.refreshCouncilMembers, memoizedUserId]);

  const contextValue = useMemo(
    () => ({
      ...state,
      setCandidateScoresArray,
      triggerCandidateScoresRefresh,
      triggerBiasTrainingRefresh,
      triggerNominationRefresh,
      triggerNominationStateRefresh,
      triggerCommitteeRefresh,
      triggerSecretariesRefresh,
      triggerCouncilMembersRefresh,
      setRefereeReports,
      setRefereeReportArchive,
      setRefreshAdminCollections,
      setRefreshNominations,
      setRefreshCommittees,
      setRefreshSecretaries,
      setRefreshCouncilMembers,
      triggerCOIRefresh,
      setCOIUpdateInProgress,
    }),
    [
      state,
      setCandidateScoresArray,
      triggerCandidateScoresRefresh,
      triggerBiasTrainingRefresh,
      triggerNominationRefresh,
      triggerNominationStateRefresh,
      triggerCommitteeRefresh,
      triggerSecretariesRefresh,
      triggerCouncilMembersRefresh,
      setRefereeReports,
      setRefereeReportArchive,
      setRefreshAdminCollections,
      setRefreshNominations,
      setRefreshCommittees,
      setRefreshSecretaries,
      setRefreshCouncilMembers,
      triggerCOIRefresh,
      setCOIUpdateInProgress,
    ],
  );

  return (
    <AdminCollections.Provider value={contextValue}>
      {children}
    </AdminCollections.Provider>
  );
};

AdminCollectionsProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useAdminCollections = () => useContext(AdminCollections);
